import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, orderBy, onSnapshot, or } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAssignments } from '../../hooks/useAssignments';
import { useClasses } from '../../hooks/useClasses';
import MobileBottomNav from './MobileBottomNav';
import DiscordConnectionPanel from '../discord/DiscordConnectionPanel';

const ResponsiveSidebar = ({ children, currentTab = 'assignments', onTabChange, userRole = 'client', onActionClick }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showDiscordPanel, setShowDiscordPanel] = useState(false);
  const { signOut, profile, user } = useAuth();
  const navigate = useNavigate();
  
  const { assignments } = useAssignments({ clientId: user?.uid });
  const { classes } = useClasses({ clientId: user?.uid });

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false); // Auto-close sidebar on desktop
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get unread message count
  useEffect(() => {
    if (!user?.uid) return;

    const userRole = profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client';
    const userAssignmentIds = assignments?.map(a => a.id) || [];
    const userClassIds = classes?.map(c => c.id) || [];
    const userItemIds = [...userAssignmentIds, ...userClassIds];

    const q = query(collection(db, 'comments'), orderBy('created_at', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));

      // Filter messages based on user role and permissions
      const filteredData = data.filter(comment => {
        if (userRole === 'admin') return true;
        return userItemIds.includes(comment.item_id);
      });

      // Count unread messages
      const unreadCount = filteredData.filter(message => 
        !message.read_by?.includes(user.uid) && message.author_id !== user.uid
      ).length;

      setUnreadMessageCount(unreadCount);
    });

    return () => unsubscribe();
  }, [user?.uid, profile, assignments, classes]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const closeSidebar = () => setIsSidebarOpen(false);

  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      { id: 'assignments', label: 'Assignments', icon: 'FileText' },
      { id: 'classes', label: 'Classes', icon: 'GraduationCap' },
      { id: 'messages', label: 'Messages', icon: 'MessageCircle', badge: unreadMessageCount > 0 ? unreadMessageCount : null },
    ];

    if (userRole === 'admin') {
      return [
        ...baseItems,
        // Removed 'users' and 'analytics' tabs as they are not implemented
      ];
    }

    return baseItems;
  };

  // Action items based on user role  
  const getActionItems = () => {
    if (userRole === 'admin') {
      return [
        { id: 'create-user', label: 'Create User', icon: 'UserPlus', action: 'createUser' },
        { id: 'search-comments', label: 'Search Comments', icon: 'Search', action: 'searchComments' },
        { 
          id: 'discord-settings', 
          label: 'Discord Settings', 
          icon: 'MessageCircle', 
          action: 'discordPanel',
          badge: profile?.discord_username ? 'connected' : null
        },
      ];
    }

    return [
      { id: 'submit-assignment', label: 'Submit Assignment', icon: 'Plus', action: 'submitAssignment' },
      { id: 'submit-class', label: 'Submit Class', icon: 'BookOpen', action: 'submitClass' },
      { 
        id: 'discord-settings', 
        label: 'Discord Settings', 
        icon: 'MessageCircle', 
        action: 'discordPanel',
        badge: profile?.discord_username ? 'connected' : null
      },
    ];
  };

  const navigationItems = getNavigationItems();
  const actionItems = getActionItems();

  const sidebarVariants = {
    closed: {
      x: '-100%',
      transition: {
        type: 'tween',
        duration: 0.3
      }
    },
    open: {
      x: 0,
      transition: {
        type: 'tween',
        duration: 0.3
      }
    }
  };

  const overlayVariants = {
    closed: {
      opacity: 0,
      pointerEvents: 'none',
      transition: {
        duration: 0.3
      }
    },
    open: {
      opacity: 1,
      pointerEvents: 'auto',
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A1A] relative overflow-x-hidden">
      {/* Background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A1A] via-[#0F0F23] to-[#0A0A1A]"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDR2MWgtNHYtMXptMC0yaDF2NGgtMXYtNHptMi0yaDF2MWgtMXYtMXptLTIgMmgtMXYxaDF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAydi0xaC0xdjFoMXptLTIgMmgtMXYxaDF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAydi0xaC0xdjFoMXptLTIgMmgtMXYxaDF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAydi0xaC0xdjFoMXptLTIgMnYtMWgtMXYxaDF6bS0yIDBoMXYxaC0xdi0xek0xMiAyMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6TTEyIDIyaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXpNMTIgMjRoNHYxaC00di0xem04IDBoNHYxaC00di0xem04IDBoNHYxaC00di0xem04IDBoNHYxaC00di0xek0xMiAyNmg0djFoLTR2LTF6bTggMGg0djFoLTR2LTF6bTggMGg0djFoLTR2LTF6bTggMGg0djFoLTR2LTF6TTEyIDI4aDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-accent/5 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Mobile Header - Hidden since we use top navigation now */}
      <header className="relative z-20 bg-black/20 backdrop-blur-lg border-b border-white/10 hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            {/* Hamburger Menu */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <Icon name="Menu" size={20} className="text-white" />
            </button>
            
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                <Icon name="GraduationCap" size={16} className="text-white" />
              </div>
              <span className="text-white font-semibold text-lg">
                {userRole === 'admin' ? 'Admin' : 'Dashboard'}
              </span>
            </div>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.location.href = '/'}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <Icon name="Home" size={18} className="text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-80 ios-card-primary border-r border-white/12 z-10" style={{borderRadius: '0 var(--ios-radius-3xl) var(--ios-radius-3xl) 0', margin: 'var(--ios-space-4) 0 var(--ios-space-4) var(--ios-space-4)'}}>
        <div className="flex flex-col w-full p-8">
          {/* Logo */}
          <div className="flex items-center space-x-4 mb-10">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center shadow-lg" style={{boxShadow: 'var(--ios-shadow-lg), var(--ios-glow-blue)'}}>
              <Icon name="GraduationCap" size={24} className="text-white" />
            </div>
            <div>
              <h1 className="ios-text-primary font-bold text-xl" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-bold)'}}>Somo Academia</h1>
              <p className="ios-text-secondary text-base capitalize" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-regular)'}}>{userRole} Dashboard</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1">
            <div className="mb-8">
              <h3 className="ios-text-tertiary text-sm uppercase tracking-wider font-semibold px-4 mb-3" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-semibold)'}}>Navigation</h3>
              <div className="space-y-1">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => onTabChange && onTabChange(item.id)}
                    className={`w-full flex items-center space-x-4 px-4 py-3.5 text-left transition-all ${
                      currentTab === item.id
                        ? 'ios-text-primary ios-card-tertiary'
                        : 'ios-text-secondary hover:bg-white/5 hover:ios-text-primary'
                    }`}
                    style={{
                      borderRadius: 'var(--ios-radius-xl)',
                      transition: 'all var(--ios-duration-fast) var(--ios-ease-out)',
                      fontFamily: 'var(--ios-font-system)',
                      fontWeight: currentTab === item.id ? 'var(--ios-weight-semibold)' : 'var(--ios-weight-medium)'
                    }}
                  >
                    <div className={`p-2 ${currentTab === item.id ? 'bg-blue-500/20' : 'bg-white/8'}`} 
                         style={{borderRadius: 'var(--ios-radius-lg)'}}>
                      <Icon name={item.icon} size={18} className={currentTab === item.id ? 'text-blue-400' : ''} />
                    </div>
                    <span className="text-base">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-2.5 py-1.5 rounded-full font-medium" 
                            style={{borderRadius: 'var(--ios-radius-full)', fontWeight: 'var(--ios-weight-semibold)'}}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-1 mb-8">
              <h3 className="ios-text-tertiary text-sm uppercase tracking-wider font-semibold px-4 mb-3" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-semibold)'}}>Quick Actions</h3>
              {actionItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.action === 'discordPanel') {
                      setShowDiscordPanel(true);
                    } else {
                      onActionClick && onActionClick(item.action);
                    }
                  }}
                  className="w-full flex items-center space-x-4 px-4 py-3.5 text-left transition-all ios-text-secondary hover:bg-white/5 hover:ios-text-primary"
                  style={{
                    borderRadius: 'var(--ios-radius-xl)',
                    transition: 'all var(--ios-duration-fast) var(--ios-ease-out)',
                    fontFamily: 'var(--ios-font-system)',
                    fontWeight: 'var(--ios-weight-medium)'
                  }}
                >
                  <div className={`p-2 bg-white/8 ${
                    item.id === 'discord-settings' ? 'bg-purple-500/15' : ''
                  }`} style={{borderRadius: 'var(--ios-radius-lg)'}}>
                    <Icon name={item.icon} size={18} className={item.id === 'discord-settings' ? 'text-purple-400' : ''} />
                  </div>
                  <span className="text-base">{item.label}</span>
                  {item.badge === 'connected' && (
                    <div className="ml-auto w-2.5 h-2.5 bg-green-400 rounded-full" style={{borderRadius: 'var(--ios-radius-full)'}} />
                  )}
                </button>
              ))}
            </div>
          </nav>

          {/* User Profile & Logout */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center space-x-3 px-3 py-2 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-accent to-secondary rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {profile?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {profile?.full_name || 'User'}
                </p>
                <p className="text-white/50 text-xs truncate">{profile?.email}</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-colors text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              <Icon name="LogOut" size={18} />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobile && isSidebarOpen && (
          <motion.div
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobile && (
          <motion.aside
            variants={sidebarVariants}
            initial="closed"
            animate={isSidebarOpen ? "open" : "closed"}
            exit="closed"
            className="fixed left-0 top-0 bottom-0 w-80 bg-black/90 backdrop-blur-xl border-r border-white/10 z-50 lg:hidden"
          >
            <div className="flex flex-col h-full p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                    <Icon name="GraduationCap" size={20} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-white font-bold text-lg">Somo Academia</h1>
                    <p className="text-white/60 text-sm capitalize">{userRole} Dashboard</p>
                  </div>
                </div>
                
                <button
                  onClick={closeSidebar}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <Icon name="X" size={20} className="text-white" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1">
                <div className="space-y-2 mb-6">
                  <h3 className="text-white/50 text-xs uppercase tracking-wider font-semibold px-3">Navigation</h3>
                  {navigationItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        onTabChange && onTabChange(item.id);
                        closeSidebar();
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        currentTab === item.id
                          ? 'bg-primary/20 text-primary border border-primary/30'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon name={item.icon} size={20} />
                      <span className="font-medium text-base">{item.label}</span>
                      {item.badge && (
                        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Actions */}
                <div className="space-y-2 mb-6">
                  <h3 className="text-white/50 text-xs uppercase tracking-wider font-semibold px-3">Actions</h3>
                  {actionItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.action === 'discordPanel') {
                          setShowDiscordPanel(true);
                        } else {
                          onActionClick && onActionClick(item.action);
                        }
                        closeSidebar();
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        item.id === 'discord-settings'
                          ? 'text-white/80 hover:bg-[#7289DA]/10 hover:text-[#7289DA]'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon name={item.icon} size={20} />
                      <span className="font-medium text-base">{item.label}</span>
                      {item.badge === 'connected' && (
                        <div className="ml-auto w-2 h-2 bg-green-400 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
              </nav>

              {/* User Profile & Logout */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center space-x-3 px-3 py-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-accent to-secondary rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">
                      {profile?.full_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {profile?.full_name || 'User'}
                    </p>
                    <p className="text-white/50 text-sm truncate">{profile?.email}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    handleLogout();
                    closeSidebar();
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-colors text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="font-medium text-base">Sign Out</span>
                </button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="relative z-10 lg:ml-80">
        <div className="pt-20 pb-24 px-3 sm:pt-20 sm:pb-8 sm:px-4 md:pt-20 md:pb-12 md:px-6 lg:pt-8 lg:pb-8 lg:px-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav currentTab={currentTab} onTabChange={onTabChange} onActionClick={onActionClick} />
      
      {/* Discord Connection Panel */}
      <DiscordConnectionPanel 
        isOpen={showDiscordPanel} 
        onClose={() => setShowDiscordPanel(false)} 
      />
    </div>
  );
};

export default ResponsiveSidebar;